import React, { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import isEmpty from 'lodash/isEmpty';

import Meta from 'Components/Utility/SEO/Meta';
import NotFound from 'Components/NotFound/NotFound';
import TakeOver from 'Components/Banner/TakeOver';
import MysteryBundleCard from 'Components/Cards/MysteryBundleCard';
import HitCard from 'Components/Cards/HitCard';
import productToHit from 'Helpers/Product/ProductToHit';

import 'Components/Mystery/MysteryPage.scss';

const MysteryEggCard = lazy(() => import(/* webpackChunkName: "MysteryEggCard" */ 'Components/Cards/MysteryEggCard'));

const messages = defineMessages({
  title: {
    id: 'mystery-page.seotitle',
    defaultMessage: 'Mystery Steam Game Bundles | Fanatical',
  },
  desc: {
    id: 'mystery-page.seodesc',
    defaultMessage: 'Explore epic gaming adventures and discover hidden gems with our expanded range of Mystery Steam Game Bundles! Offering you even more ways to uncover amazing games at unbeatable prices.',
  },
});

export default function MysteryPage() {
  const { formatMessage } = useIntl();
  const all = useSelector(state => state.mystery.all);

  if (isEmpty(all)) return <NotFound />;

  const topMystery = all.filter(item => item.mystery_homepage);
  const supportingMystery = all.filter(item => !item.mystery_homepage && !item.mystery_upsell);

  // Convert supporting mystery products to use the HitCard
  const supportingMysteryHits = supportingMystery.map(productToHit);

  return (
    <React.Fragment>
      <Meta
        title={formatMessage(messages.title)}
        description={formatMessage(messages.desc)}
        url="mystery"
        pageType="CollectionPage"
        hits={all}
      />

      <TakeOver appearsOn="mystery" />

      <main className="MysteryPage">
        <section className="container">
          <h1 className="pb-2">
            <FormattedMessage
              id="mystery-bundles.title"
              defaultMessage="Mystery Steam Game Bundles"
            />
          </h1>
          <p className="seo-blurb-text mb-0">
            <FormattedMessage
              id="mystery-bundles.introduction.one"
              defaultMessage="Get ready to dive into epic gaming adventures and uncover hidden gems with our exciting range of Mystery Bundles! We're bringing you even more ways to find incredible games at prices that will leave you grinning ear to ear."
            />
          </p>
        </section>

        {!isEmpty(topMystery) && (topMystery[0].slug === 'mystery-egg-bundle' || topMystery[0].slug === 'mystery-box-bundle') &&
          <Suspense>
            <div className="container">
              <MysteryEggCard product={topMystery[0]} />
            </div>
          </Suspense>
        }

        {!isEmpty(topMystery) && topMystery[0].slug !== 'mystery-egg-bundle' && topMystery[0].slug !== 'mystery-box-bundle' &&
          <MysteryBundleCard product={topMystery[0]} />
        }

        <section className="container">
          <h2 className="pb-2">
            <FormattedMessage
              id="mystery-bundles.sub-title.two"
              defaultMessage="Explore our expanded mystery bundles"
            />
          </h2>
          <p className="seo-blurb-text mb-0">
            <FormattedMessage
              id="mystery-bundles.introduction.two"
              defaultMessage="Beyond our popular monthly mystery bundle, we offer a variety of options tailored for every type of gamer, with fresh titles added regularly. Let our mystery bundles bring a bit of randomness into your gaming experience, because sometimes, the best adventures are the ones you never see coming!"
            />
          </p>
        </section>

        {!isEmpty(supportingMysteryHits) &&
          <section className="container">
            <div className="row">
              {supportingMysteryHits.map(hit =>
                <HitCard
                  hit={hit}
                  key={hit.product_id}
                  xsSize={6}
                  smSize={6}
                  mdSize={4}
                  lgSize={4}
                />,
              )}
            </div>
          </section>
        }
      </main>
    </React.Fragment>
  );
}
